import React, { useEffect, useState, Fragment } from 'react';
import './landing.scss';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import clsx from 'clsx';
import { CONTRACTS } from '@/constants';
import { ImageWithFallback, Modal } from '@/components';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { TimeSpan } from '@/utility/time-span.ts';

interface ContractResponse {
	id?: number;
	name: string;
	description: string;
	contractName: string;
	contractLink: string;
}

interface ContractData {
	id: number;
	name: string;
	description: string;
	contractName: string;
	contractLink: string;
}

const MENU_SECTIONS = {
	ADVANTAGES: 'avantajlar',
	WHOAPPLY: 'kimler-basvurabilir',
	SSS: 'sikca-sorulan-sorular'
};

const DopingHafizaLandingPage: React.FC = () => {
	const [activeContract, setActiveContract] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [contractContent, setContractContent] = useState<ContractData | null>(null);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
	const [contractCache, setContractCache] = useState<Record<string, ContractData>>({});
	const { ContractControllerApiApplicant } = useApi();

	const navigation = useNavigate();

	const mapResponseToContractData = (response: ContractResponse): ContractData => {
		return {
			id: response.id || 0,
			name: response.name,
			description: response.description,
			contractName: response.contractName,
			contractLink: response.contractLink
		};
	};

	const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
		e.preventDefault();
		const section = document.getElementById(sectionId);
		if (section) {
			section.scrollIntoView({ behavior: 'smooth' });
		}
	};

	const onContractChange = (e: React.MouseEvent<HTMLButtonElement>, contract: string) => {
		e.preventDefault();
		setActiveContract(contract);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const createAccountType = (e: any, params: string) => {
		e.preventDefault();
		navigation(NavigationPath.createAccount, {
			state: { accountType: params }
		});
	};

	useEffect(() => {
		if (isModalOpen && activeContract) {
			if (contractCache[activeContract]) {
				setContractContent(contractCache[activeContract]);
				setIsLoading(false);
			} else {
				setIsLoading(true);
			}
		}
	}, [isModalOpen, activeContract, contractCache]);

	useQueryProvider([QUERY_KEYS.findContractApplicant, activeContract], () => ContractControllerApiApplicant.findContract(activeContract), {
		enabled: !!activeContract && isModalOpen && !contractCache[activeContract],
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		onSuccess: data => {
			if (data) {
				const contractData = mapResponseToContractData(data as ContractResponse);
				setContractContent(contractData);
				setContractCache(prev => ({
					...prev,
					[activeContract as string]: contractData
				}));
				setIsLoading(false);
			}
		},
		onError: () => {
			setIsLoading(false);
		}
	});

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const handleMobileMenuItem = (e: React.MouseEvent, sectionId?: string) => {
		e.preventDefault();
		toggleMobileMenu();

		if (sectionId) {
			setTimeout(() => scrollToSection(e, sectionId), TimeSpan.milliSecond.one);
		}
	};

	useEffect(() => {
		// Initialize the counter animation from the original JS
		const initCounters = () => {
			const counters = document.querySelectorAll('.counter');

			const observer = new IntersectionObserver(
				(entries, observer) => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							updateCount(entry.target as HTMLElement);
							observer.unobserve(entry.target);
						}
					});
				},
				{
					threshold: 0.1
				}
			);

			counters.forEach(counter => {
				observer.observe(counter);
			});

			function updateCount(counter: HTMLElement) {
				const target = Number(counter.getAttribute('data-target'));
				let count = Number(counter.innerText.replace(/,/g, ''));

				let inc = target / 200;
				if (target > 1000000) {
					inc = target / 100;
				} else if (target > 100000) {
					inc = target / 150;
				}

				if (count < target) {
					count += inc;
					counter.innerText = Math.floor(count).toString();
					requestAnimationFrame(() => updateCount(counter));
				} else {
					const symbol = counter.classList.contains('counter-tl') ? '₺' : '+';
					counter.innerText = target.toLocaleString() + symbol;
				}
			}
		};

		// Initialize the FAQ accordion functionality
		const initAccordion = () => {
			const accordions = document.querySelectorAll('.faq-accordion');
			accordions.forEach(accordion => {
				accordion.addEventListener('click', () => {
					const answer = accordion.querySelector('.faq-answer') as HTMLElement;
					const answerParagraph = accordion.querySelector('.faq-answer p') as HTMLElement;
					const opener = accordion.querySelector('.faq-opener') as HTMLElement;
					const closer = accordion.querySelector('.faq-closer') as HTMLElement;

					if (answer.style.display === 'none' || !answer.style.display) {
						answer.style.display = 'block';
						if (answerParagraph) answerParagraph.style.display = 'block';
						opener.style.display = 'none';
						closer.style.display = 'flex';
					} else {
						answer.style.display = 'none';
						if (answerParagraph) answerParagraph.style.display = 'none';
						opener.style.display = 'flex';
						closer.style.display = 'none';
					}
				});
			});
		};

		// Run initialization functions after the component mounts
		initCounters();
		initAccordion();

		// Cleanup function
		return () => {
			const accordions = document.querySelectorAll('.faq-accordion');
			accordions.forEach(accordion => {
				accordion.removeEventListener('click', () => {});
			});
		};
	}, []);

	useEffect(() => {
		if (window.location.hash) {
			const id = window.location.hash.substring(1);
			const element = document.getElementById(id);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}, []);

	useEffect(() => {
		const isWebView = navigator.userAgent.includes('3p-webview');

		const mobileMenuFooter = document.querySelector('.header__mobile-menu-footer') as HTMLElement;

		if (mobileMenuFooter) {
			if (isWebView) {
				mobileMenuFooter.style.display = 'block';
			} else {
				mobileMenuFooter.style.display = 'none';
			}
		}
	}, []);

	return (
		<Fragment>
			<div className="webflow-styles">
				<div
					className={clsx('header__overlay', {
						'is-visible': isMobileMenuOpen
					})}
					onClick={toggleMobileMenu}></div>

				<div
					className={clsx('header__mobile-menu header__applicant-menu', {
						'is-open': isMobileMenuOpen
					})}>
					<div className="header__mobile-menu-header">
						<div className="header__mobile-menu-header-logo">
							<ImageWithFallback
								className="app-logo"
								alt="Doping Hafıza Logo"
								src="dh-birebir-logo.svg"
							/>
						</div>
						<button
							className="header__mobile-menu-header-close"
							onClick={toggleMobileMenu}
							aria-label="Close menu">
							×
						</button>
					</div>

					<div className="header__mobile-menu-content">
						<ul className="header__mobile-menu-list">
							<li className="header__mobile-menu-item">
								<a
									href="/landing#"
									className="header__mobile-menu-link"
									onClick={() => toggleMobileMenu()}>
									Doping Hafıza Bire Bir Nedir?
								</a>
							</li>
							<li className="header__mobile-menu-item">
								<a
									href="/landing#avantajlar"
									className="header__mobile-menu-link"
									onClick={e => {
										handleMobileMenuItem(e, MENU_SECTIONS.ADVANTAGES);
									}}>
									Avantajları Nelerdir?
								</a>
							</li>
							<li className="header__mobile-menu-item">
								<a
									href="/landing#kimler-basvurabilir"
									className="header__mobile-menu-link"
									onClick={e => {
										handleMobileMenuItem(e, MENU_SECTIONS.WHOAPPLY);
									}}>
									Kimler Başvurabilir?
								</a>
							</li>
							<li className="header__mobile-menu-item">
								<a
									href="/landing#sikca-sorulan-sorular"
									className="header__mobile-menu-link"
									onClick={e => {
										handleMobileMenuItem(e, MENU_SECTIONS.SSS);
									}}>
									Sıkça Sorulan Sorular
								</a>
							</li>
						</ul>
					</div>
					<div className="header__mobile-menu-footer">
						<button
							className="btn btn--primary"
							onClick={() => {
								toggleMobileMenu();
								navigation(NavigationPath.login);
							}}>
							Giriş Yap
						</button>
					</div>
				</div>
				<section className="header-webflow">
					<div className="w-layout-blockcontainer main-wrapper header-webflow w-container">
						<button
							className={clsx('header__hamburger', {
								'is-active': isMobileMenuOpen
							})}
							onClick={toggleMobileMenu}
							aria-label="Toggle menu">
							<span className="header__hamburger-line"></span>
							<span className="header__hamburger-line"></span>
							<span className="header__hamburger-line"></span>
						</button>
						<div className="logo-wrapper">
							<img
								className="app-logo"
								src="/images/expert-logo.svg"
								loading="lazy"
								alt=""
							/>
						</div>
						<div className="menu-items-wrapper">
							<a
								className="menu-item"
								onClick={e => scrollToSection(e, MENU_SECTIONS.ADVANTAGES)}>
								Avantajları Neler?
							</a>
							<a
								href="#kimler-basvurabilir"
								className="menu-item"
								onClick={e => scrollToSection(e, MENU_SECTIONS.WHOAPPLY)}>
								Kimler Başvurabilir?
							</a>
							<a
								href="#sikca-sorulan-sorular"
								className="menu-item"
								onClick={e => scrollToSection(e, MENU_SECTIONS.SSS)}>
								S.S.S.
							</a>
							<a
								href="#"
								className="main-button secondarybutton w-button"
								onClick={() => navigation(NavigationPath.login)}>
								Giriş Yap
							</a>
							<a
								href="#"
								className="main-button w-button"
								onClick={() => navigation(NavigationPath.createAccount)}>
								Başvur
							</a>
						</div>
					</div>
				</section>

				<section className="section-webflow">
					<div className="w-layout-blockcontainer main-wrapper w-container top-container">
						<div className="w-layout-grid _2-column">
							<div className="vertical-block hero wflow-grid-col-6">
								<h3 className="above-sub-headline">Eğitimde Yeni Bir Dönem:</h3>
								<h1 className="h1">Doping Hafıza Bire Bir Uzman</h1>
								<p className="p center-align-text">
									Mesleki anlamda deneyimlerini zenginleştirirken kazancını arttırmak mı istiyorsun?
									<br />‍<br />
									Öyleyse; Doping Hafıza Bire Bir Uzman'a katılarak yüz binlerce öğrencinin eğitim yolculuklarına eşlik etmeye ne dersin?
								</p>
								<div className="store-images-wrapper-top">
									<a
										href="https://apps.apple.com/us/app/doping-haf%C4%B1za-bire-bir-uzman/id6744922173"
										target="_blank"
										rel="noopener noreferrer">
										<img
											src="/images/appstorelogo.svg"
											alt="App Store'dan İndir"
											className="store-badge"
											loading="lazy"
										/>
									</a>
									<a
										href="https://play.google.com/store/apps/details?id=com.dopinghafiza.birebiruzman&pcampaignid=web_share"
										target="_blank"
										rel="noopener noreferrer">
										<img
											src="/images/playstorelogo.svg"
											alt="Google Play'den İndir"
											className="store-badge"
											loading="lazy"
										/>
									</a>
								</div>
								<a
									className="main-button w-button"
									onClick={e => scrollToSection(e, 'avantajlar')}>
									Daha Fazla
								</a>
							</div>
							<div className="image-wrapper wflow-grid-col-6">
								<img
									src="/images/hero-image.png"
									loading="lazy"
									sizes="(max-width: 540px) 100vw, 540px"
									srcSet="/images/hero-image-p-500.png 500w, /images/hero-image.png 540w"
									alt=""
									className="desktop-banner"
								/>
								<img
									src="/images/hero-mobile.png"
									loading="lazy"
									alt=""
									className="mobile-banner"
								/>
							</div>
						</div>
					</div>
				</section>

				<section className="section-webflow section-2">
					<div className="w-layout-blockcontainer main-wrapper w-container">
						<div className="w-layout-grid _2-column flip-wrap">
							<div className="card-block wflow-grid-col-6">
								<div className="card-wrapper">
									<div className="numbers-card">
										<img
											src="/images/kayitli-rehber-icon.png"
											loading="lazy"
											alt=""
											className="teacher-icon"
										/>
										<div className="numbers-card-text">
											<h5
												data-target="2500"
												className="heading h5 counter">
												0
											</h5>
											<div className="text-block numbers-text">Kayıtlı Koç</div>
										</div>
									</div>
									<div className="numbers-card">
										<img
											src="/images/kayitli-egitmen-icon.png"
											loading="lazy"
											alt=""
											className="teacher-icon"
										/>
										<div className="numbers-card-text">
											<h5
												data-target="3500"
												className="heading h5 counter">
												0
											</h5>
											<div className="text-block numbers-text">Eğitmen</div>
										</div>
									</div>
								</div>
								<div className="card-wrapper second-section">
									<div className="numbers-card">
										<img
											src="/images/ozel-kocluk-icon.png"
											loading="lazy"
											alt=""
											className="class-icon"
										/>
										<div className="numbers-card-text">
											<h6
												data-target="462416"
												className="heading h6 counter">
												0
											</h6>
											<div className="text-block numbers-text">Özel Koçluk</div>
										</div>
									</div>
									<div className="numbers-card">
										<img
											src="/images/ozel-kocluk-icon.png"
											loading="lazy"
											alt=""
											className="class-icon"
										/>
										<div className="numbers-card-text">
											<h5
												data-target="293487"
												className="heading h6 counter">
												0
											</h5>
											<div className="text-block numbers-text">Özel Ders</div>
										</div>
									</div>
									<div className="numbers-card">
										<img
											src="/images/ozel-kocluk-icon.png"
											loading="lazy"
											alt=""
											className="class-icon"
										/>
										<div className="numbers-card-text">
											<h5 className="heading h6">1m+</h5>
											<div className="text-block numbers-text">Mutlu Öğrenci</div>
										</div>
									</div>
								</div>
							</div>
							<div className="vertical-block vertical-align-conteiner wflow-grid-col-6">
								<h2 className="h2">
									<code className="code">Binlerce uzman</code> ve yüz binlerce öğrenci burada!
								</h2>
								<p className="p center-align-text">Türkiye'nin en büyük öğrenme platformunda yüz binlerce öğrenci ile bire bir görüş.</p>
							</div>
						</div>
					</div>
				</section>

				<section
					id="avantajlar"
					className="section-webflow section-2">
					<div className="w-layout-blockcontainer main-wrapper w-container">
						<div className="w-layout-grid _2-column">
							<div className="vertical-block wflow-grid-col-7">
								<h3 className="above-sub-headline">Doping Hafıza Bire Bir Uzman ile</h3>
								<h2 className="h2">Seni bekleyen avantajlar ne?</h2>
								<p className="p description-content">Zaman kazandıracak ve öğretim sürecinizi kolaylaştıracak kullanıma hazır materyaller sizi bekliyor!</p>
								<div className="advantage-list-item">
									<img
										src="/images/Users-Group-Two-Rounded.svg"
										loading="lazy"
										alt=""
									/>
									<p className="p ">Türkiye&#x27;nin en büyük öğrenme platformunun parçası ol.</p>
								</div>
								<div className="advantage-list-item">
									<img
										src="/images/Wallet-Money.svg"
										loading="lazy"
										alt=""
									/>
									<p className="p">Görüşme başına cüzdanına tanımlanan ücretler ile aylık gelirini hızlıca yükselt.</p>
								</div>
								<div className="advantage-list-item">
									<img
										src="/images/Global.svg"
										loading="lazy"
										alt=""
									/>
									<p className="p">Görüşme çerçeveleri doğrultusunda istediğin yer ve zamanda öğrencilere ulaş.</p>
								</div>
								<div className="advantage-list-item">
									<img
										src="/images/Medal-Ribbon-Star.svg"
										loading="lazy"
										alt=""
									/>
									<p className="p">Binlerce öğrenci ile görüşerek özgeçmişini zenginleştir, iş fırsatlarını yakala.</p>
								</div>
								<div className="advantage-list-item">
									<img
										src="/images/Diploma.svg"
										loading="lazy"
										alt=""
									/>
									<p className="p">Mesleki ve kişisel gelişimini destekleyen eğitimlere ücretsiz katıl.</p>
								</div>
							</div>
							<div className="image-wrapper advantage-image wflow-grid-col-5">
								<div className="floating-card">
									<div className="collapse-inside">
										<img
											src="/images/uzman-profil4.png"
											loading="lazy"
											alt=""
											className="collapse-image"
										/>
										<img
											src="/images/uzman-profil1.png"
											loading="lazy"
											alt=""
											className="collapse-image"
										/>
										<img
											src="/images/uzman-profil2.png"
											loading="lazy"
											alt=""
											className="collapse-image"
										/>
										<img
											src="/images/uzman-profil3.png"
											loading="lazy"
											alt=""
											className="collapse-image"
										/>
									</div>
									<h5
										data-target="7000"
										className="heading h6 counter">
										7.000
									</h5>
									<div className="text-block numbers-text">Uzman Kullanıyor</div>
								</div>
								<div className="floating-card income-card">
									<img
										src="/images/lira-icon.svg"
										loading="lazy"
										alt=""
										className="image-5"
									/>
									<div className="numbers-card-text">
										<h5
											data-target="80130019"
											className="heading h6 counter counter-tl">
											₺80.130.019
										</h5>
										<div className="text-block numbers-text">Kazanç sağlandı</div>
									</div>
								</div>
								<img
									src="/images/telefon-mockup-gorsel.png"
									loading="lazy"
									sizes="(max-width: 546px) 100vw, 546px"
									srcSet="/images/telefon-mockup-gorsel-p-500.png 500w, /images/telefon-mockup-gorsel.png 546w"
									alt=""
									className="image"
								/>
							</div>
						</div>
					</div>
				</section>

				<section
					id="kimler-basvurabilir"
					className="section-webflow section-2">
					<div className="w-layout-blockcontainer main-wrapper w-container">
						<h2 className="h2">Kimler Başvurabilir</h2>
						<p className="p">Uzmanlık koşullarını sağlayan kişiler her şehirden başvuru gerçekleştirebilirler.</p>
						<div className="w-layout-grid grid egitmen-container-desktop">
							<div className="teacher-card">
								<img
									src="/images/brans-ogretmeni-photo.png"
									loading="lazy"
									alt=""
									className="teacher-image"
								/>
								<h6 className="teacher-headline">Branş Uzmanı</h6>
								<p className="p teacher-desc">Üniversitelerin Eğitim ya da Fen Edebiyat Fakültelerinden mezun olmuş kişilerdir.</p>
								<a
									href="#"
									className="main-button w-button apply-btn"
									onClick={e => createAccountType(e, 'INSTRUCTOR')}>
									Başvur
								</a>
							</div>
							<div className="teacher-card">
								<img
									src="/images/rehberlik-uzmani-photo.png"
									loading="lazy"
									alt=""
								/>
								<h6 className="teacher-headline">Rehberlik Uzmanı</h6>
								<p className="p teacher-desc">Üniversitelerin Psikoloji ve ya Psikolojik Danışmanlık ve Rehberlik bölümlerinden mezun olmuş kişilerdir.</p>
								<a
									href="#"
									className="main-button w-button  apply-btn"
									onClick={e => createAccountType(e, 'COACH')}>
									Başvur
								</a>
							</div>
							<div className="teacher-card">
								<img
									src="/images/universiteli-koc-photo.png"
									loading="lazy"
									alt=""
								/>
								<h6 className="teacher-headline">Üniversiteli Koç</h6>
								<p className="p teacher-desc">Üniversite sınavında derece yapmış üniversite öğrencileridir.</p>
								<p className="p lowest-desc">
									<ul>
										<li>*Sayılsal ilk 20bin</li>
										<li>*E.A. ilk 10bin</li>
										<li>*Sözel ve Y. Dil ilk 5bin</li>
									</ul>
								</p>
								<a
									href="#"
									className="main-button w-button  apply-btn"
									onClick={e => createAccountType(e, 'UNIVERSITY_STUDENT')}>
									Başvur
								</a>
							</div>
						</div>
						<div className="w-layout-grid grid egitmen-container-mobile">
							<div className="teacher-card">
								<img
									src="/images/brans-ogretmeni-photo.png"
									loading="lazy"
									alt=""
									className="teacher-image"
								/>
								<h6 className="teacher-headline">Branş Uzmanı</h6>
								<p className="p teacher-desc">Üniversitelerin Eğitim ya da Fen Edebiyat Fakültelerinden mezun olmuş kişilerdir.</p>
								<a
									href="#"
									className="main-button w-button apply-btn"
									onClick={e => createAccountType(e, 'INSTRUCTOR')}>
									Başvur
								</a>
							</div>
							<div className="teacher-card">
								<img
									src="/images/rehberlik-uzmani-photo.png"
									loading="lazy"
									alt=""
								/>
								<h6 className="teacher-headline">Rehberlik Uzmanı</h6>
								<p className="p teacher-desc">Üniversitelerin Psikoloji ve ya Psikolojik Danışmanlık ve Rehberlik bölümlerinden mezun olmuş kişilerdir.</p>
								<a
									href="#"
									className="main-button w-button apply-btn"
									onClick={e => createAccountType(e, 'COACH')}>
									Başvur
								</a>
							</div>
							<div className="teacher-card">
								<img
									src="/images/universiteli-koc-photo.png"
									loading="lazy"
									alt=""
								/>
								<h6 className="teacher-headline">Üniversiteli Koç</h6>
								<p className="p teacher-desc">Üniversite sınavında derece yapmış üniversite öğrencileridir.</p>
								<p className="p lowest-desc">*Sayılsal ilk 20bin *E.A. ilk 10bin *Sözel ve Y. Dil ilk 5bin</p>
								<a
									href="#"
									className="main-button w-button apply-btn"
									onClick={e => createAccountType(e, 'UNIVERSITY_STUDENT')}>
									Başvur
								</a>
							</div>
						</div>
					</div>
				</section>

				<section
					id="sikca-sorulan-sorular"
					className="section-webflow section-5">
					<div className="w-layout-blockcontainer main-wrapper w-container">
						<div className="w-layout-grid _2-column">
							<div className="vertical-block wflow-grid-col-6">
								<h2 className="h2">Sıkça Sorulan Sorular</h2>
								<p className="p">Merak edilen tüm soruların cevaplarına anında ulaşabilirsin.</p>
								<div className="email-block">
									<p className="p lowest-desc">Aradığınızı bulamadınız ya da sorunuz mu var?</p>
									<p className="p textlink">info@dopinghafızabirebir.com</p>
								</div>
							</div>
							<div className="vertical-block wflow-grid-col-6">
								<div
									data-w-id="23ce78d4-dfe9-b3aa-b98a-b2e4a2c09b8d"
									className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Doping Hafıza Bire Bir Uzman Nedir?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Doping Teknoloji ürünü olan Doping Hafıza Bire Bir Uzman; öğrencilere uzmanlar tarafından, zorlandığı konularda bire bir ders anlatımı, koçluk desteği
												ve soru çözümü sunan yeni nesil öğrenme platformudur.
											</p>
										</div>
									</div>
								</div>

								{/* Additional FAQ items */}
								<div
									data-w-id="67ea7bcb-90a9-8fe3-b658-f9584b45c69d"
									className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">
											<strong>Doping Hafıza Bire Bir Uzman’da Hangi Konularda Destek Verebilirim?</strong>
										</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Doping Hafıza Bire Bir Uzman’da branş uzmanı, rehberlik uzmanı ve derece yapmış üniversite öğrencileri olarak hizmet verebilirsiniz. Branş uzmanı
												olarak, alanınız ile ilgili konularda canlı dersler verebilir ya da öğrencilerin göndermiş olduğu soruları video çözerek cevaplayabilirsiniz. Rehberlik
												Uzmanı olarak, öğrencilerin okul ve sınav süreçlerindeki başarılarını arttırmak amacıyla öğrencilere ve velilere rehberlik desteği sunabilirsiniz.
												Derece yapmış üniversite öğrencileri olarak, öğrencilere canlı dersler verebilir ya da soru çözümü yapabilirsiniz.
											</p>
										</div>
									</div>
								</div>

								<div className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Doping Hafıza Bire Bir Uzman’da Hangi Yaş Grubuna Uzmanlık Yapabilirim?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Doping Hafıza Bire Bir Uzman’da, gerekli şartları sağladığınız durumda, ilkokuldan başlayarak, ortaokul, lise, KPSS, DGS ve ALES seviyesindeki
												öğrencilere destek vermeniz mümkün.
											</p>
										</div>
									</div>
								</div>

								<div className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Doping Hafıza Birebir Uzman’ı Hangi Saatlerde Kullanabilirim?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Canlı ders ya da koçluk desteği vermek isterseniz; uygulama, haftanın 7 günü saat 10.00-23.00 arasında açık olmaktadır. Soru çözümü yapmak isterseniz
												haftanın 7 günü 24 saat boyunca soru çözümü gerçekleştirebilirsiniz.
											</p>
										</div>
									</div>
								</div>

								<div className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Doping Hafıza Bire Bir Uzman’a Haftada Kaç Saatimi Ayırmam Gerekir?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Uygulamayı kullanırken herhangi bir saat limiti yoktur. Uygulamanın aktif olduğu saatlerde öğrencilerle buluşabilirsiniz. Uygulamada olmanız gereken bir
												alt limit olmamak ile birlikte, uygulamaya günde en az 2 saatinizi ayırmak uzmanlık sürecinizin gelişimini hızlandırmaktadır.
											</p>
										</div>
									</div>
								</div>

								<div className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Ödemeler nasıl gerçekleşir?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Uygulama üzerinde E-cüzdanınız bulunmaktadır. Yapmış olduğunuz her görüşme anlık olarak cüzdanınıza yansır. Her ayın 1-5’i arasında kazanmış olduğunuz
												tutar hesabınıza yansır.
											</p>
										</div>
									</div>
								</div>

								<div className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Doping Hafıza Bire Bir Uzman’a Hangi Cihazdan Giriş Yapmalıyım?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Doping Hafıza Bire Bir Uzman, masaüstü, dizüstü bilgisayar, tablet ve cep telefonu üzerinden rahatlıkla çalışmaktadır. Kendinize uygun herhangi bir
												cihaz üzerinden uygulamaya giriş sağlayabilirsiniz.
											</p>
										</div>
									</div>
								</div>

								{/* Add remaining FAQ items here */}
								<div className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Doping Hafıza Bire Bir Uzman’ı kullanırken Hangi Teknolojik Donanımlara İhtiyacım Var?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">
												Uygulamayı kullanırken; cihazınızın kamerası, hoparlörü ve kulaklığı çalışıyor olmalıdır ve herhangi bir internet sorunu yaşamamanız gerekir.
											</p>
										</div>
									</div>
								</div>

								<div className="faq-accordion">
									<div
										style={{ display: 'none' }}
										className="faq-closer">
										<img
											src="/images/minus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-opener">
										<img
											src="/images/plus-icon.svg"
											loading="lazy"
											alt=""
										/>
									</div>
									<div className="faq-question-wrapper">
										<p className="faq-question">Doping Hafıza Bire Bir Uzman’a Kayıt Olurken Ücret Ödeyecek miyim?</p>
										<div
											style={{ display: 'none' }}
											className="faq-answer">
											<p className="p faq-answer">Doping Hafıza Bire Bir Uzman’a kayıt olurken ve ilerleyen hiçbir süreçte herhangi bir ücret ödemeniz gerekmemektedir.</p>
										</div>
									</div>
								</div>
							</div>
							<div className="email-block mobile-visible">
								<p className="p lowest-desc">Aradığınızı bulamadınız ya da sorunuz mu var?</p>
								<p className="p textlink">info@dopinghafızabirebir.com</p>
							</div>
						</div>
					</div>
				</section>

				<section className="section-webflow footer-webflow">
					<div className="w-layout-blockcontainer main-wrapper w-container footer-contain">
						<div className="w-layout-grid footer-webflow-webflow-grid webflow-footer">
							<div className="vertical-block vertical-align-conteiner wflow-grid-col-4">
								<img
									className="app-logo"
									src="/images/expert-logo.svg"
									loading="lazy"
									alt=""
								/>
								<p className="p center-align-text footer-webflow-grey-text">
									Doping Hafıza Bire Bir Uzman Türkiye'nin dört bir yanındaki branş ve rehber uzmanlarını öğrencilerle buluşturan yeni nesil rehberlik ve özel ders uygulamasıdır.
								</p>
								<div className="social-wrap">
									<img
										src="/images/Linkedin.png"
										loading="lazy"
										alt=""
									/>
									<img
										src="/images/Instagram.png"
										loading="lazy"
										alt=""
									/>
									<img
										src="/images/X.png"
										loading="lazy"
										alt=""
									/>
								</div>
							</div>
							<div className="vertical-block vertical-align-conteiner wflow-grid-col-4 flex-item-end">
								<a
									href="#avantajlar"
									className="menu-item footer-webflow-grey-text"
									onClick={e => scrollToSection(e, MENU_SECTIONS.ADVANTAGES)}>
									Avantajları Neler?
								</a>
								<a
									href="#kimler-basvurabilir"
									className="menu-item footer-webflow-grey-text"
									onClick={e => scrollToSection(e, MENU_SECTIONS.WHOAPPLY)}>
									Kimler Başvurabilir?
								</a>
								<a
									href="#sikca-sorulan-sorular"
									className="menu-item footer-webflow-grey-text"
									onClick={e => scrollToSection(e, MENU_SECTIONS.SSS)}>
									S.S.S.
								</a>
								<button
									className="menu-item footer-webflow-grey-text"
									style={{
										background: 'none',
										border: 'none',
										cursor: 'pointer',
										textAlign: 'left',
										padding: 0
									}}
									onClick={e => onContractChange(e as React.MouseEvent<HTMLButtonElement>, CONTRACTS.APPLICANT_MEMBERSHIP_AGREEMENT)}>
									Bire Bir Uzman Üyelik Koşulları
								</button>
								<button
									className="menu-item footer-webflow-grey-text"
									style={{
										background: 'none',
										border: 'none',
										cursor: 'pointer',
										textAlign: 'left',
										padding: 0
									}}
									onClick={e => onContractChange(e as React.MouseEvent<HTMLButtonElement>, CONTRACTS.CLARIFICATION_TEXT)}>
									Aydınlatma Metni
								</button>
							</div>
							<div className="vertical-block vertical-align-conteiner wflow-grid-col-4 flex-item-end">
								<div className="store-images-wrapper">
									<a
										href="https://apps.apple.com/us/app/doping-haf%C4%B1za-bire-bir-uzman/id6744922173"
										target="_blank"
										rel="noopener noreferrer">
										<img
											src="/images/appstorelogo.svg"
											alt="App Store'dan İndir"
											className="store-badge"
											loading="lazy"
										/>
									</a>
									<a
										href="https://play.google.com/store/apps/details?id=com.dopinghafiza.birebiruzman&pcampaignid=web_share"
										target="_blank"
										rel="noopener noreferrer">
										<img
											src="/images/playstorelogo.svg"
											alt="Google Play'den İndir"
											className="store-badge"
											loading="lazy"
										/>
									</a>

									{/*
                  AppGallery

                  <a href="#" target="_blank" rel="noopener noreferrer">
                    <img
                        src="/images/appgallerystorelogo.svg"
                        alt="AppGallery'den İndir"
                        className="store-badge"
                        loading="lazy"
                    />

                    </a> */}
								</div>
							</div>
						</div>
						<div className="w-layout-grid footer-webflow-grid copyright">
							<div className="vertical-block wflow-grid-col-5">
								<img
									src="/images/doping-technology-logo.png"
									loading="lazy"
									alt=""
									className="image-3"
								/>
							</div>
							<div className="vertical-block wflow-grid-col-7">
								<p className="p copyright">© 2023 Doping Hafıza Bire Bir Uzman | Tüm Hakları Saklıdır. ©Doping Hafıza Bire Bir, Doping Technology ürünüdür.</p>
							</div>
						</div>
					</div>
				</section>
			</div>
			<Modal
				onClose={closeModal}
				size="lg"
				contentClassName="contract-modal-container-landing"
				closeOnClickOutside
				variant="centered"
				isOpen={isModalOpen}>
				{isLoading ? (
					<div className="loading-contract">Sözleşme yükleniyor...</div>
				) : contractContent ? (
					<div className="contract-modal-content">
						<i
							className="icons-close-circle"
							onClick={closeModal}></i>
						{contractContent.contractLink && (
							<>
								<div
									className={clsx('contract-iframe-container', {
										'full-container': activeContract === CONTRACTS.CLARIFICATION_TEXT
									})}>
									<iframe
										src={contractContent.contractLink}
										title={contractContent.name}
									/>
								</div>
							</>
						)}
					</div>
				) : (
					<div className="loading-contract">Sözleşme bulunamadı</div>
				)}
			</Modal>
		</Fragment>
	);
};

export default DopingHafizaLandingPage;
